import React, {useState, useEffect, useRef} from 'react';
import {
  Box,
  Paper,
  Typography,
  Grid,
  CssBaseline,
  CircularProgress,
  Button,
  Alert,
} from '@mui/material';
import {CheckCircle, Error, Email} from '@mui/icons-material';
import {useNavigate, useSearchParams} from 'react-router-dom';
import newLogo from 'src/assets/logo_no_background.png';
import {userPublicServiceVerifyEmail} from 'src/apis/autoGenerate/services';
import message from 'src/components/Message';

interface VerificationState {
  status: 'loading' | 'success' | 'error' | 'invalid';
  message: string;
  userData?: {
    user: {
      id: string;
      email: string;
      firstName: string;
      lastName: string;
      status: string;
    };
    organization: {
      id: string;
      name: string;
      code: string;
      description: string;
    };
  };
}

export default function VerifyEmail() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [verificationState, setVerificationState] = useState<VerificationState>({
    status: 'loading',
    message: 'Verifying your email address...',
  });

  // Add ref to track if verification has been attempted
  const hasVerified = useRef(false);

  useEffect(() => {
    const verifyEmail = async () => {
      // Prevent duplicate calls
      if (hasVerified.current) {
        return;
      }
      hasVerified.current = true;

      const token = searchParams.get('token');
      const userId = searchParams.get('userId');

      // Validate required parameters
      if (!token || !userId) {
        setVerificationState({
          status: 'invalid',
          message: 'Invalid verification link. Please check your email and try again.',
        });
        return;
      }

      try {
        const response = await userPublicServiceVerifyEmail({
          userId,
          token,
        });

        setVerificationState({
          status: 'success',
          message: 'Email verified successfully! Your account has been activated.',
          userData: response.data.data,
        });

        // Show success message
        message.success('Email verified successfully! You can now log in to your account.');
      } catch (error: any) {
        console.error('Email verification failed:', error);

        let errorMessage = 'Email verification failed. Please try again.';

        // Handle specific error cases
        if (error.response?.status === 400) {
          errorMessage = 'Invalid or expired verification link.';
        } else if (error.response?.status === 404) {
          errorMessage = 'User not found or verification link is invalid.';
        } else if (error.response?.data?.message) {
          errorMessage = error.response.data.message;
        }

        setVerificationState({
          status: 'error',
          message: errorMessage,
        });
      }
    };

    verifyEmail();
  }, [searchParams]);

  const handleGoToLogin = () => {
    navigate('/login');
  };

  const handleResendVerification = () => {
    // You might want to implement a resend verification email functionality
    message.info('Please contact support to resend verification email.');
  };

  const getStatusIcon = () => {
    switch (verificationState.status) {
      case 'loading':
        return <CircularProgress size={64} color="primary" />;
      case 'success':
        return <CheckCircle sx={{fontSize: 64, color: 'success.main'}} />;
      case 'error':
      case 'invalid':
        return <Error sx={{fontSize: 64, color: 'error.main'}} />;
      default:
        return <Email sx={{fontSize: 64, color: 'grey.500'}} />;
    }
  };

  const getStatusColor = () => {
    switch (verificationState.status) {
      case 'success':
        return 'success';
      case 'error':
      case 'invalid':
        return 'error';
      default:
        return 'info';
    }
  };

  return (
    <Grid container component="main" sx={{height: '100vh'}}>
      <CssBaseline />
      <Grid item xs={12} sm={8} md={5} component={Paper} elevation={6} square>
        <Box
          sx={{
            my: 8,
            mx: 4,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
          }}
        >
          <Box sx={{mb: 4}}>
            <img src={newLogo} alt="Logo" style={{height: '128px'}} />
          </Box>

          <Typography component="h1" variant="h5" sx={{mb: 2}}>
            Email Verification
          </Typography>

          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              width: '100%',
              maxWidth: 400,
            }}
          >
            {/* Status Icon */}
            <Box sx={{mb: 3}}>{getStatusIcon()}</Box>

            {/* Status Message */}
            <Alert severity={getStatusColor()} sx={{mb: 3, width: '100%'}} variant="outlined">
              <Typography variant="body1" align="center">
                {verificationState.message}
              </Typography>
            </Alert>

            {/* Success Details */}
            {verificationState.status === 'success' && verificationState.userData && (
              <Box sx={{mb: 3, width: '100%'}}>
                <Typography variant="h6" gutterBottom align="center" color="success.main">
                  Welcome, {verificationState.userData.user.firstName}!
                </Typography>
                <Typography variant="body2" align="center" color="text.secondary">
                  Your account for <strong>{verificationState.userData.organization.name}</strong>{' '}
                  has been successfully verified.
                </Typography>
              </Box>
            )}

            {/* Action Buttons */}
            <Box sx={{display: 'flex', flexDirection: 'column', gap: 2, width: '100%'}}>
              {verificationState.status === 'success' && (
                <Button fullWidth variant="contained" size="large" onClick={handleGoToLogin}>
                  Go to Login
                </Button>
              )}

              {(verificationState.status === 'error' || verificationState.status === 'invalid') && (
                <>
                  <Button fullWidth variant="contained" size="large" onClick={handleGoToLogin}>
                    Go to Login
                  </Button>
                  <Button
                    fullWidth
                    variant="outlined"
                    size="large"
                    onClick={handleResendVerification}
                  >
                    Contact Support
                  </Button>
                </>
              )}

              {verificationState.status === 'loading' && (
                <Typography variant="body2" align="center" color="text.secondary">
                  Please wait while we verify your email address...
                </Typography>
              )}
            </Box>
          </Box>
        </Box>
      </Grid>

      {/* Right section - Welcome Message */}
      <Grid
        item
        xs={false}
        sm={4}
        md={7}
        sx={{
          position: 'relative',
          bgcolor: '#f5f8fa',
          display: {xs: 'none', sm: 'block'},
        }}
      >
        <Box
          sx={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            bgcolor: '#e8f0fe',
            backgroundImage: 'url(/abstract-pattern.png)',
            backgroundSize: 'cover',
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
            alignItems: 'center',
            p: 4,
          }}
        >
          <Typography variant="h3" fontWeight="bold" color="#1A4065" mb={3}>
            Welcome to entrio
          </Typography>
          <Typography variant="h6" color="#555" textAlign="center" maxWidth={600}>
            Smart property management solutions for modern communities
          </Typography>
          <Typography variant="body1" color="#777" textAlign="center" maxWidth={600} mt={2}>
            Your email verification is being processed. Once verified, you'll have full access to
            your account.
          </Typography>
        </Box>
      </Grid>
    </Grid>
  );
}
